import type { Currency } from '@openeducation/pp-rn-shared-types';
import { styled } from '@openeducation/pp-rn-themes';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { TextInputProps, TextInput, PPText } from '../../../atoms';
import { InputRow } from '../../../molecules';

interface PaymentInfoAmountProps extends TextInputProps {
  error?: string;
  helperText?: string | null;
  currency?: Currency;
}

const PaymentInfoAmount = ({
  onFocus,
  onBlur,
  value,
  onValueChange,
  error,
  disabled,
  helperText,
  currency,
}: PaymentInfoAmountProps) => {
  const { t } = useTranslation();

  return (
    <InputRow label={t('input.amount.label')} error={error}>
      <InputContainer>
        <AmountInputContainer disabled={disabled}>
          {currency && (
            <CurrencyPrefix disabled={disabled}>{currency} </CurrencyPrefix>
          )}
          <StyledTextInput
            name="amount"
            value={value}
            disabled={disabled}
            keyboardType="numeric"
            placeholder={t('input.amount.label') as string}
            onFocus={onFocus}
            onBlur={onBlur}
            onValueChange={onValueChange}
          />
        </AmountInputContainer>
        {helperText && <HelperText>{helperText}</HelperText>}
      </InputContainer>
    </InputRow>
  );
};

const InputContainer = styled.View`
  width: 100%;
`;

interface AmountInputContainerProps {
  disabled?: boolean;
}

const AmountInputContainer = styled.View<AmountInputContainerProps>`
  flex-direction: row;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding-horizontal: 14px;
  min-height: 32px;
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.colors.dark0 : 'transparent'};
`;

interface CurrencyPrefixProps {
  disabled?: boolean;
}

const CurrencyPrefix = styled(PPText).attrs({
  fontSize: 14,
})<CurrencyPrefixProps>`
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.disabled : theme.colors.dark1};
  opacity: ${({ disabled }) => (disabled ? 0.8 : 1)};
`;

const HelperText = styled(PPText).attrs({
  fontSize: 12,
  fontType: 'light',
})`
  margin-top: 4px;
`;

const StyledTextInput = styled(TextInput)`
  flex: 1;
  border: none;
  border-width: 0;
  outline: none;
  outline-width: 0;
  padding-horizontal: 0;
`;

export default memo(PaymentInfoAmount);
